import {
  AHText,
  AHTextFonts,
  Box,
  Circle,
  useLayoutContext,
  useTheme,
} from "arkh";
import React, { PropsWithChildren, useMemo, useState } from "react";
import { Dimensions, Platform, StyleSheet, View } from "react-native";

import * as Haptics from "expo-haptics";
import { DateTime } from "luxon";

import * as d from "d3-scale";
import * as dtime from "d3-time";
import * as shape from "d3-shape";
import Svg, { Line, Path, Rect, Text } from "react-native-svg";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
  SharedValue,
  useAnimatedProps,
  useAnimatedReaction,
  useSharedValue,
} from "react-native-reanimated";
import { useSizeClass } from "../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { FormattedMessage } from "react-intl";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { scheduleOnRN } from "react-native-worklets";

const AnimatedReact = Animated.createAnimatedComponent(Rect);

function HighlightBar(props: {
  barX: number[];
  activeIndex: SharedValue<number>;
  innerHeight: number;
}) {
  const theme = useTheme();
  const animatedProps = useAnimatedProps(() => {
    return {
      x: props.barX[props.activeIndex.value] - 7,
    };
  });

  return (
    <AnimatedReact
      {...props}
      width={14}
      opacity={0.1}
      fill={theme.primary}
      height={props.innerHeight}
      animatedProps={animatedProps}
    />
  );
}
export function YearlyCountStats({
  createdData,
  resolvedData,
}: {
  createdData: readonly { readonly date: string; readonly count: number }[];
  resolvedData: readonly { readonly date: string; readonly count: number }[];
}) {
  const ul = useLayoutContext();
  const [width, setWidth] = useState(
    Dimensions.get("window").width - ul.space.medium * 2 - ul.space.xsmall * 2
  );

  const height = 280;
  const theme = useTheme();

  const wide = useSizeClass().width === SizeClass.REGULAR;

  const endDate = DateTime.local().plus({ week: 1 }).startOf("week").toJSDate();
  const startDate = DateTime.local()
    .minus({ months: wide ? 12 : 6 })
    .endOf("week")
    .toJSDate();
  const paddingHorizontal = ul.space.large;
  const scaleX = useMemo(
    () =>
      d.scaleTime(
        [startDate, endDate],
        [paddingHorizontal * 2, width - paddingHorizontal]
      ),
    [endDate, paddingHorizontal, startDate, width]
  );

  const _createdData = createdData.map((cd) => ({
    date: DateTime.fromISO(cd.date),
    count: cd.count,
  }));
  const _resolvedData = resolvedData.map((cd) => ({
    date: DateTime.fromISO(cd.date),
    count: cd.count,
  }));

  const historyData = dtime.timeWeeks(startDate, endDate).map((d) => {
    const timeStep = DateTime.fromJSDate(d).startOf("week");

    const created =
      _createdData.find((cd) => cd.date.equals(timeStep))?.count ?? 0;
    const resolved =
      _resolvedData.find((rd) => rd.date.equals(timeStep))?.count ?? 0;

    return {
      date: d,
      created,
      resolved,
      max: Math.max(created, resolved, 0),
    };
  });

  const paddingVertical = 6;

  const innerHeight = height - paddingVertical * 2;
  const scaleY = d
    .scaleLinear()
    .domain([Math.max(...historyData.map((l) => l.max + 1)), 0])
    .range([height - paddingVertical * 3, 0])
    .nice(5);

  const barWidth = 6;

  const barX = useMemo(
    () => historyData.map((h) => scaleX(h.date) + barWidth / 2),
    [historyData, scaleX]
  );

  const line = shape
    .line<(typeof historyData)[0]>()
    .curve(shape.curveMonotoneX)
    .x((d) => scaleX(d.date) + barWidth / 2)
    .y((d) => innerHeight - scaleY(d.resolved) - paddingVertical)(historyData);

  const activeIndex = useSharedValue(historyData.length - 1);
  const panGesture = Gesture.Pan()
    .activeOffsetX([-20, 20])
    .onUpdate((d) => {
      const diffs = barX.map((h) => Math.abs(h - d.x));
      const min = Math.min(...diffs);
      const index = diffs.indexOf(min);

      if (index !== -1) {
        if (activeIndex.value !== index) {
          activeIndex.value = index;
          if (Platform.OS === "ios") {
            scheduleOnRN(
              Haptics.impactAsync,
              Haptics.ImpactFeedbackStyle.Medium
            );
          }
        }
      }
    });

  const tapGesture = Gesture.Tap().onStart((d) => {
    const diffs = barX.map((h) => Math.abs(h - d.x));
    const min = Math.min(...diffs);
    const index = diffs.indexOf(min);

    if (index !== -1) {
      if (activeIndex.value !== index) {
        activeIndex.value = index;
        if (Platform.OS === "ios") {
          scheduleOnRN(Haptics.impactAsync, Haptics.ImpactFeedbackStyle.Medium);
        }
      }
    }
  });

  return (
    <GestureDetector gesture={Gesture.Exclusive(panGesture, tapGesture)}>
      <Animated.View>
        <Box>
          <Header activeIndex={activeIndex} history={historyData} />
          <View
            style={{ flex: 1, marginTop: 20 }}
            onLayout={(e) => {
              setWidth(e.nativeEvent.layout.width);
            }}
          >
            <Svg
              style={{ flex: 1, backgroundColor: "#fff" }}
              pointerEvents="none"
              width={width}
              height={height}
            >
              <HighlightBar
                innerHeight={innerHeight - paddingVertical}
                activeIndex={activeIndex}
                barX={barX}
              />
              {scaleX.ticks(dtime.timeMonth.every(1)!).map((tick, i) => {
                return (
                  <Line
                    key={tick.toISOString()}
                    y1={innerHeight - paddingVertical}
                    y2={0}
                    x1={scaleX(tick)}
                    strokeWidth={StyleSheet.hairlineWidth}
                    stroke="#bbb"
                    x2={scaleX(tick)}
                  />
                );
              })}

              {historyData.map(({ created, date }, i) => {
                const y = Math.max(1, scaleY(created));
                if (created === 0) {
                  return null;
                }
                return (
                  <Rect
                    key={date.toISOString()}
                    rx={2}
                    width={barWidth}
                    stroke="#000"
                    fill="#000"
                    height={y}
                    y={innerHeight - y - paddingVertical}
                    x={scaleX(date)}
                  />
                );
              })}

              {scaleX.ticks(dtime.timeMonth.every(1)!).map((tick, i) => {
                return (
                  <Text
                    key={tick.toISOString()}
                    y={innerHeight + 10}
                    x={scaleX(tick)}
                    textAnchor="middle"
                    fill="#000"
                    fontFamily={AHTextFonts.MEDIUM as any}
                    fontSize={7}
                  >
                    {DateTime.fromJSDate(tick).toLocaleString({
                      month: "short",
                    })}
                  </Text>
                );
              })}
              {scaleY.ticks(5).map((tick, i) => {
                const y = scaleY(tick);
                return (
                  <Text
                    key={i + ""}
                    dy={innerHeight - y + paddingVertical - 5}
                    x={paddingHorizontal}
                    fill="#000"
                    textAnchor="middle"
                    fontFamily={AHTextFonts.MEDIUM as any}
                    fontSize={8}
                  >
                    {tick}
                  </Text>
                );
              })}
              {line ? (
                <Path
                  d={line}
                  stroke={theme.getColor(3)}
                  strokeWidth={2}
                  fill="none"
                  disabled
                />
              ) : null}
            </Svg>
          </View>
        </Box>
      </Animated.View>
    </GestureDetector>
  );
}

function Header(props: {
  activeIndex: SharedValue<number>;
  history: readonly { date: Date; created: number; resolved: number }[];
}) {
  const [activeIndex, setActiveIndex] = useState(props.activeIndex.value ?? 0);
  useAnimatedReaction(
    () => props.activeIndex.value,
    (index, prev) => {
      if (index !== prev) {
        scheduleOnRN(setActiveIndex, index);
      }
    }
  );
  const theme = useTheme();
  if (props.history.length === 0) {
    return null;
  }
  const item = props.history[activeIndex];
  if (!item) {
    return null;
  }

  return (
    <HeaderContainer>
      <HeaderItemDetail
        color="black"
        label={
          <FormattedMessage
            description="IssueStats Reported label"
            defaultMessage="Reported"
          />
        }
        value={item.created}
      />
      <HeaderItemDetail
        color={theme.getColor(3)}
        label={
          <FormattedMessage
            description="IssueStats Resolved label"
            defaultMessage="Resolved"
          />
        }
        value={item.resolved}
      />
      <HeaderItemWeekDateTitle date={item.date} />
    </HeaderContainer>
  );
}

function HeaderContainer(props: PropsWithChildren<unknown>) {
  return (
    <Box
      style={{ height: 40, borderBottomWidth: 1 }}
      spaceHorizontal="large"
      borderColor="border-primary"
    >
      <Box
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {props.children}
      </Box>
    </Box>
  );
}

function HeaderItemDetail(props: {
  value: number;
  label: React.ReactNode;
  color: string;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "baseline",
        width: "33%",
        justifyContent: "flex-start",
      }}
    >
      <Circle size="dot" style={{ backgroundColor: props.color }} />
      <AnimatedCount count={props.value} />
      <AHText type="detail"> {props.label}</AHText>
    </View>
  );
}

function AnimatedCount(props: { count: number }) {
  return (
    <AHText spaceLeft="xsmall" style={{ width: 42 }} type="title">
      {props.count}
    </AHText>
  );
}

function HeaderItemWeekDateTitle(props: { date: Date }) {
  const intl_tokens = useIntlTokens();
  return (
    <AHText type="subtitle" style={{ width: "33%", textAlign: "right" }}>
      {intl_tokens.Week} {DateTime.fromJSDate(props.date).toFormat("W")}
    </AHText>
  );
}
